// =============================================
// Schema.org orchestrator + pure builders + injector + validator.
// OUTPUT: jeden <script type="application/ld+json"> z @graph (root = WebPage).
// PBN-friendly: BEZ Organization / WebSite / publisher / worksFor / isPartOf / logo.
// =============================================

import { supabase } from "@/integrations/supabase/client";
import { logPublic, logDebug } from "@/services/contentEngine/contentLogger";
import { slugify } from "./socialPlatforms";
import type {
  SchemaPerson,
  SchemaBlogPosting,
  SchemaFAQPage,
  SchemaBreadcrumb,
  SchemaImageObject,
  SchemaWebPage,
  SchemaProfilePage,
  SchemaPayload,
  SchemaGraph,
  GraphNode,
  PersonContext,
  BlogPostingContext,
  WebPageContext,
  ProfilePageContext,
  ValidationResult,
} from "./types";

const SCHEMA_TAG_OPEN = '<script type="application/ld+json">';
const SCHEMA_TAG_CLOSE = "</script>";

// ───────────────────────────────────────────────────────────
// Pure builders — input data, output object
// ───────────────────────────────────────────────────────────

export function buildImageObjectSchema(
  canonicalUrl: string,
  imageUrl: string | null | undefined,
  caption?: string,
): SchemaImageObject | null {
  if (!imageUrl) return null;
  return {
    "@type": "ImageObject",
    "@id": `${canonicalUrl}#primaryimage`,
    url: imageUrl,
    contentUrl: imageUrl,
    ...(caption ? { caption } : {}),
  };
}

export function buildPersonSchema(ctx: PersonContext): SchemaPerson {
  const personId = ctx.authorUrl ? `${ctx.authorUrl}#person` : `#person-${ctx.authorSlug}`;
  const schema: SchemaPerson = {
    "@type": "Person",
    "@id": personId,
    name: ctx.publicLabel,
  };
  if (ctx.authorUrl) schema.url = ctx.authorUrl;
  const cleanImage = cleanAuthorImage(ctx.image ?? null);
  if (cleanImage) schema.image = cleanImage;
  if (ctx.jobTitle) schema.jobTitle = ctx.jobTitle;
  if (ctx.bioHtml) {
    const desc = smartTrim(stripHtml(ctx.bioHtml), 500);
    if (desc) schema.description = desc;
  }
  if (ctx.knowsAbout && ctx.knowsAbout.length > 0) schema.knowsAbout = ctx.knowsAbout;
  const cleanedSameAs = cleanSameAs(ctx.sameAs);
  if (cleanedSameAs.length > 0) schema.sameAs = cleanedSameAs;
  return schema;
}

// STOP-D': C1 ProfilePage schema dla dedykowanej strony autora.
// Hybrid @graph: ProfilePage (root) + Person (standalone z @id ref).
// NIE inline Person jak w BlogPosting.author — to standalone node.
export function buildProfilePageSchema(ctx: ProfilePageContext): SchemaPayload {
  if (!ctx.profileUrl || !ctx.publicLabel || !ctx.authorSlug) {
    return {
      graph: null,
      warnings: [],
      errors: ["ProfilePage requires profileUrl, publicLabel, authorSlug"],
    };
  }

  const person = buildPersonSchema({
    publicLabel: ctx.publicLabel,
    authorUrl: ctx.authorUrl || ctx.profileUrl,
    authorSlug: ctx.authorSlug,
    image: ctx.image,
    jobTitle: ctx.jobTitle,
    bioHtml: ctx.bioHtml,
    knowsAbout: ctx.knowsAbout,
    sameAs: ctx.sameAs,
  });

  const profilePage: SchemaProfilePage = {
    "@type": "ProfilePage",
    "@id": `${ctx.profileUrl}#profilepage`,
    url: ctx.profileUrl,
    name: `O autorze: ${ctx.publicLabel}`,
    mainEntity: { "@id": person["@id"] },
  };

  if (ctx.description) profilePage.description = ctx.description;
  if (ctx.dateCreated) profilePage.dateCreated = ctx.dateCreated;
  if (ctx.dateModified) profilePage.dateModified = ctx.dateModified;

  return {
    graph: {
      "@context": "https://schema.org",
      "@graph": [profilePage, person],
    },
    warnings: [],
    errors: [],
  };
}

export function buildBreadcrumbSchema(
  canonicalUrl: string,
  siteUrl: string,
  homeName: string,
  headline: string,
  seedBreadcrumb?: { name: string; url: string } | null,
): SchemaBreadcrumb {
  const items: SchemaBreadcrumb["itemListElement"] = [
    { "@type": "ListItem", position: 1, name: homeName || "Strona główna", item: siteUrl.replace(/\/$/, "") + "/" },
  ];
  if (seedBreadcrumb?.name && seedBreadcrumb?.url) {
    items.push({
      "@type": "ListItem",
      position: items.length + 1,
      name: seedBreadcrumb.name,
      item: seedBreadcrumb.url,
    });
  }
  items.push({
    "@type": "ListItem",
    position: items.length + 1,
    name: headline,
    item: canonicalUrl,
  });
  return {
    "@type": "BreadcrumbList",
    "@id": `${canonicalUrl}#breadcrumb`,
    itemListElement: items,
  };
}

export function buildBlogPostingSchema(
  ctx: BlogPostingContext,
  author: SchemaPerson,
  primaryImageId: string | null,
): SchemaBlogPosting {
  const siteBase = ctx.website.url.replace(/\/$/, "");
  const nodeUrl = buildCanonicalUrl(siteBase, ctx.node.slug);

  // FIX 2026-05-18: headline = visible H1 (ctx.node.title), NIE seo_title.
  // seo_title jest osobnym polem idącym TYLKO do Yoast meta `_yoast_wpseo_title`
  // (handluje publishQueueService). Schema headline ma matchować H1 na stronie
  // żeby Google / AI Overview nie widziały rozbieżności między title w schema
  // a widzialnym tytułem artykułu.
  const headline = smartTrim(ctx.node.title, 110);
  const dates = resolveDates(ctx.content);

  const schema: SchemaBlogPosting = {
    "@type": "BlogPosting",
    "@id": `${nodeUrl}#blogposting`,
    url: nodeUrl,
    mainEntityOfPage: { "@id": `${nodeUrl}#webpage` },
    headline,
    description: "",
    author,
    datePublished: dates.datePublished,
    dateModified: dates.dateModified,
    inLanguage: normalizeLanguage(ctx.map.language),
    isAccessibleForFree: true,
  };

  const desc = buildDescription(
    ctx.content.meta_description,
    null, // seo_description — kolumna nie istnieje obecnie w thematic_node_content
    ctx.content.final_html,
    headline,
  );
  if (desc) {
    schema.description = desc;
  } else {
    // smartTrim placeholder zamiast pustego — walidator wymaga description present.
    // Gdy wszystkie source puste, użyj headline jako last-resort minimal description.
    schema.description = headline;
  }

  if (primaryImageId) {
    schema.image = { "@id": primaryImageId };
  }
  if (ctx.articleSection) {
    schema.articleSection = ctx.articleSection;
  }

  // CLEANUP 2026-05-18 wieczór: usunięte about/mentions/keywords.
  // Powód: surowy KG (content_knowledge_graphs) halucynował przypadkowe encje
  // (Marcin Dębowski / SELECTED / Double Digital / Growth Hub dla artykułu
  // o social media growth) bez sprawdzenia czy są w final_html. Zamiast
  // złożonego validation flow → wycinamy pola całkowicie. Schema core wystarczy:
  // WebPage + BlogPosting + Person inline + ImageObject + BreadcrumbList.

  // 2026-07-21: `about` (top 3 entities wg Entity Salience) usuniete razem z calym
  // modulem A4 — schema wraca do stanu po cleanupie z 2026-05-18.

  return schema;
}

export function buildWebPageSchema(
  ctx: WebPageContext,
  blogPostingId: string,
  breadcrumbId: string,
  primaryImageId: string | null,
  // STOP-F (A1 Pillar): optional hasPart referencujac spoke Article schemas (@id refs).
  // TYLKO dla pillar page (caller checks document_role='pillar' + flag VITE_PILLAR_FLOW_ENABLED).
  // Spoke WebPage NIGDY nie ma hasPart.
  childArticleIds?: string[],
): SchemaWebPage {
  const schema: SchemaWebPage = {
    "@type": "WebPage",
    "@id": `${ctx.canonicalUrl}#webpage`,
    url: ctx.canonicalUrl,
    name: ctx.headline,
    inLanguage: normalizeLanguage(ctx.language),
    datePublished: ctx.datePublished,
    dateModified: ctx.dateModified,
    breadcrumb: { "@id": breadcrumbId },
    mainEntity: { "@id": blogPostingId },
  };
  if (primaryImageId) {
    schema.primaryImageOfPage = { "@id": primaryImageId };
  }
  // STOP-F: hasPart array — pillar referencuje spoke article @ids
  if (childArticleIds && childArticleIds.length > 0) {
    schema.hasPart = childArticleIds.map((id) => ({ "@id": id }));
  }
  return schema;
}

// FAQPage builder — exported but NOT invoked by orchestrator.
// Enable only when a website explicitly opts in AND HTML contains a visible FAQ block.
// (Faza 2 może wprowadzić flag `website.faq_schema_enabled` + auto-detect.)
export function buildFAQPageSchema(outline: unknown, html: string, baseUrl: string): SchemaFAQPage | null {
  const sections = (outline as { sections?: Array<{ h3s?: Array<{ format?: string; header?: string }> }> })?.sections;
  if (!Array.isArray(sections)) return null;

  const questions: string[] = [];
  for (const section of sections) {
    if (!Array.isArray(section.h3s)) continue;
    for (const h3 of section.h3s) {
      if (h3.format === "question" && typeof h3.header === "string" && h3.header.includes("?")) {
        questions.push(h3.header);
      }
    }
  }

  if (questions.length < 3) return null;

  const mainEntity = questions
    .map((q) => {
      const answer = extractAnswerFromHtml(html, q);
      if (!answer || answer.length < 50) return null;
      return {
        "@type": "Question" as const,
        name: q,
        acceptedAnswer: { "@type": "Answer" as const, text: answer.slice(0, 1000) },
      };
    })
    .filter((x): x is NonNullable<typeof x> => x !== null);

  if (mainEntity.length < 3) return null;

  return {
    "@type": "FAQPage",
    "@id": `${baseUrl}#faq`,
    mainEntity,
  };
}

// ───────────────────────────────────────────────────────────
// Orchestrator
// ───────────────────────────────────────────────────────────

export async function generateSchemasForNode(nodeId: string): Promise<SchemaPayload> {
  const warnings: string[] = [];
  const errors: string[] = [];

  const { data: node, error: nodeErr } = await (supabase as any)
    .from("thematic_nodes")
    .select("id, title, slug, seed_id, canonical_question, map_id, primary_entity_raw, document_role, node_role")
    .eq("id", nodeId)
    .single();

  if (nodeErr || !node) {
    errors.push(`Node ${nodeId} not found`);
    return { graph: null, warnings, errors };
  }

  const { data: content } = await (supabase as any)
    .from("thematic_node_content")
    .select("final_html, meta_description, seo_title, outline, featured_image_url, published_at, updated_at, engine_debug, effective_publish_date")
    .eq("node_id", nodeId)
    .single();

  if (!content?.final_html) {
    errors.push("Content not ready (no final_html)");
    return { graph: null, warnings, errors };
  }

  const { data: map } = await (supabase as any)
    .from("thematic_maps")
    .select("id, name, language, website_id, active_profile_id")
    .eq("id", node.map_id)
    .single();

  if (!map) {
    errors.push("Map not found");
    return { graph: null, warnings, errors };
  }

  const { data: website } = await (supabase as any)
    .from("websites")
    .select("id, url, name, schema_enabled")
    .eq("id", map.website_id)
    .single();

  if (!website) {
    errors.push("Website not found (map.website_id missing or invalid)");
    return { graph: null, warnings, errors };
  }

  if (website.schema_enabled === false) {
    warnings.push("Website has schema_enabled=false, skipping injection");
    return { graph: null, warnings, errors };
  }

  if (!website.url || !website.name) {
    errors.push("Website missing url or name");
    return { graph: null, warnings, errors };
  }

  const { data: profile } = map.active_profile_id
    ? await (supabase as any)
        .from("thematic_writing_profiles")
        .select("*")
        .eq("id", map.active_profile_id)
        .single()
    : { data: null };

  // ── Person — pełny inline w BlogPosting.author. NO worksFor (bez Org).
  const siteBase = website.url.replace(/\/$/, "");
  const personCtx = buildPersonContext(profile, website, siteBase);
  if (!profile) {
    warnings.push("Person: brak active writing profile dla mapy (fallback z website.name)");
  } else if (profile.schema_enabled !== true) {
    warnings.push("Person: profile.schema_enabled=false → fallback minimal (name z website.name)");
  } else if (!profile.public_author_label) {
    warnings.push("Person: brak public_author_label → fallback minimal");
  }
  const person = buildPersonSchema(personCtx);

  // ── Seed breadcrumb (Home → Seed/Kategoria → Artykuł)
  let seedBreadcrumb: { name: string; url: string } | null = null;
  if (node.seed_id) {
    const { data: seed } = await (supabase as any)
      .from("thematic_seeds")
      .select("name, slug, hub_title, hub_slug_override")
      .eq("id", node.seed_id)
      .single();
    if (seed) {
      const seedSlug = (seed as { hub_slug_override?: string; slug?: string }).hub_slug_override
        || (seed as { slug?: string }).slug;
      const seedName = (seed as { hub_title?: string; name?: string }).hub_title
        || (seed as { name?: string }).name;
      if (seedSlug && seedName) {
        seedBreadcrumb = {
          name: seedName,
          url: `${siteBase}/category/${seedSlug}/`,
        };
      }
    }
  }

  // ── Canonical URL / headline / dates — shared dla WebPage + BlogPosting.
  // buildCanonicalUrl gwarantuje trailing slash + brak `//` (guard przeciw `//#` w @id).
  // FIX 2026-05-18: headline = visible H1 (node.title) — NIGDY seo_title.
  // seo_title idzie OSOBNO do Yoast meta przez publishQueueService.
  // Visible H1 trafia do: BlogPosting.headline, WebPage.name, BreadcrumbList[last].name, ImageObject.caption.
  const canonicalUrl = buildCanonicalUrl(siteBase, node.slug);
  const visibleTitle = smartTrim(node.title, 110);
  const language = normalizeLanguage(map.language);
  const dates = resolveDates(content);
  const datePublished = dates.datePublished;
  const dateModified = dates.dateModified;

  // ── Primary image (osobny ImageObject + reference z BlogPosting.image + WebPage.primaryImageOfPage)
  // caption = visible H1, NIE seo_title
  const primaryImage = buildImageObjectSchema(canonicalUrl, content.featured_image_url, visibleTitle);

  // ── BlogPosting (deep nested author, brak about/mentions/keywords — cleanup 2026-05-18)
  const blogCtx: BlogPostingContext = {
    node: {
      id: node.id,
      title: node.title,
      slug: node.slug,
      seed_id: node.seed_id,
      canonical_question: node.canonical_question,
      primary_entity: node.primary_entity_raw,
    },
    content: {
      final_html: content.final_html,
      meta_description: content.meta_description,
      seo_title: content.seo_title,
      featured_image_url: content.featured_image_url,
      outline: content.outline,
      // Backdated publish date ma priorytet nad faktycznym published_at.
      // Pozwala schema.datePublished match WP post.date_gmt gdy user backdate'ował partię.
      published_at: content.effective_publish_date || content.published_at,
      updated_at: content.updated_at,
      // Pole oryginalne — używane przez resolveDates BACKDATE RULE (dateModified = datePublished
      // gdy effective set, żeby uniknąć footprint "backdated + immediate edit 6 dni później").
      effective_publish_date: content.effective_publish_date,
    },
    map: { id: map.id, name: map.name, language: map.language },
    website: { url: website.url, name: website.name },
    articleSection: seedBreadcrumb?.name || null,
    seedBreadcrumb,
  };

  // STOP-F (A1): pillar gate — TYLKO dla document_role='pillar' fetch child article ids.
  // 2026-07-21: pobieranie top-3 encji wg salience usuniete razem z modulem A4 (`about`).
  const isPillarSchema = node.document_role === "pillar" || node.node_role === "pillar";
  let childArticleIds: string[] | undefined;

  if (isPillarSchema) {
    // Child article @ids — spoke nodes z mapy ktore maja published WP post
    const { data: spokeContents } = await (supabase as any)
      .from("thematic_nodes")
      .select(`
        id, slug, document_role,
        thematic_node_content!inner(engine_stage),
        wp_posts:matched_wp_post_id(wp_url, status)
      `)
      .eq("map_id", map.id)
      .neq("id", nodeId)
      .eq("thematic_node_content.engine_stage", "ready");

    if (spokeContents && spokeContents.length > 0) {
      childArticleIds = (spokeContents as any[])
        .filter((s) => {
          const wp = Array.isArray(s.wp_posts) ? s.wp_posts[0] : s.wp_posts;
          return wp?.wp_url && wp.status === "publish";
        })
        .map((s: any) => {
          const wp = Array.isArray(s.wp_posts) ? s.wp_posts[0] : s.wp_posts;
          return `${(wp.wp_url as string).replace(/\/$/, "")}#blogposting`;
        });
    }
  }

  const blogPosting = buildBlogPostingSchema(
    blogCtx,
    person,
    primaryImage?.["@id"] || null,
  );

  // ── Breadcrumb (position 3 name = visible H1, NIE seo_title)
  const breadcrumb = buildBreadcrumbSchema(
    canonicalUrl,
    siteBase,
    website.name,
    visibleTitle,
    seedBreadcrumb,
  );

  // ── WebPage (root, ale teraz tylko trzyma @id refs do innych nodes w @graph)
  // name = visible H1 — NIE seo_title (zgodność z BlogPosting.headline + WP H1)
  const webPageCtx: WebPageContext = {
    canonicalUrl,
    headline: visibleTitle,
    language,
    datePublished,
    dateModified,
    siteUrl: siteBase,
    homeName: website.name,
    primaryImageUrl: content.featured_image_url,
    primaryImageCaption: visibleTitle,
    seedBreadcrumb,
  };
  const webPage = buildWebPageSchema(
    webPageCtx,
    blogPosting["@id"],
    breadcrumb["@id"],
    primaryImage?.["@id"] || null,
    childArticleIds, // STOP-F: undefined dla spoke, array dla pillar
  );

  // ── @graph hybrid (2026-05-17 wieczór, 3-cia iteracja):
  //   - WebPage/BlogPosting/ImageObject/BreadcrumbList = osobne nodes z @id refs
  //   - Person = INLINE w BlogPosting.author (NIE osobny top-level node)
  //
  // Powód: checker rich-result nie resolvował BlogPosting.author=@id do osobnego
  // Person node → BLOCKING error "missing author.name". Inline rozwiązuje bez
  // wracania do deep nesting WebPage.mainEntity (które miało analogiczny problem
  // dla BlogPosting). Person to leaf entity, więc inline OK; reszta = @id refs.
  const graphNodes: GraphNode[] = [webPage, blogPosting];
  if (primaryImage) graphNodes.push(primaryImage);
  graphNodes.push(breadcrumb);

  // ── FAQPage — DISABLED by default. Faza 2 może wprowadzić flag.
  // (User explicit: nie traktuj FAQ jako rich-result hacka.)

  const graph: SchemaGraph = {
    "@context": "https://schema.org",
    "@graph": graphNodes,
  };

  return { graph, warnings, errors };
}

// ───────────────────────────────────────────────────────────
// Person context — fallback chain z profile lub minimal z website
// ───────────────────────────────────────────────────────────

function buildPersonContext(
  profile: Record<string, unknown> | null | undefined,
  website: { url: string; name: string },
  siteBase: string,
): PersonContext {
  // Person.name priority chain — REAL imię/nazwisko, NIE identity/label:
  //   1. profile.profile_name (typowo "Dorian Zawadzki")
  //   2. profile.name (alias kolumny)
  //   3. profile.public_author_label (fallback dla legacy profili gdy brak name)
  const profileNameRaw = typeof profile?.profile_name === "string" ? profile.profile_name.trim() : "";
  const profileNameAlt = typeof profile?.name === "string" ? profile.name.trim() : "";
  const profileLabel = typeof profile?.public_author_label === "string" ? profile.public_author_label.trim() : "";
  const personName = profileNameRaw || profileNameAlt || profileLabel;
  const profileEnabled = profile?.schema_enabled === true;
  const useProfile = !!profile && profileEnabled && !!personName;

  if (useProfile) {
    const publicLabel = personName;
    const providedSlug = typeof profile.author_slug === "string" && profile.author_slug ? profile.author_slug : null;
    const authorSlug = providedSlug || slugify(publicLabel) || "autor";
    const providedUrl = typeof profile.author_url === "string" && profile.author_url ? profile.author_url : null;
    const authorUrl = providedUrl || `${siteBase}/autor/${authorSlug}/`;

    const knowsAboutRaw = Array.isArray(profile.author_knows_about) ? (profile.author_knows_about as string[]) : [];
    const knowsAbout = knowsAboutRaw.length > 0 ? knowsAboutRaw : extractCoreEntities(profile.core_entities);
    const sameAs = Array.isArray(profile.author_same_as) ? (profile.author_same_as as string[]) : [];

    return {
      publicLabel,
      authorUrl,
      authorSlug,
      image: typeof profile.author_photo_url === "string" ? profile.author_photo_url : null,
      jobTitle: typeof profile.author_job_title === "string" ? profile.author_job_title : null,
      bioHtml: typeof profile.author_bio_html === "string" ? profile.author_bio_html : null,
      knowsAbout,
      sameAs,
    };
  }

  // Fallback minimal — Person.name = website.name, url = siteBase
  const authorSlug = slugify(website.name) || "redakcja";
  return {
    publicLabel: website.name,
    authorUrl: `${siteBase}/autor/${authorSlug}/`,
    authorSlug,
  };
}

// ───────────────────────────────────────────────────────────
// Injector — wstrzykuje jeden <script> z @graph na PIERWSZA pozycje HTML
// ───────────────────────────────────────────────────────────

export function injectSchemasIntoHtml(html: string, graph: SchemaGraph | null): string {
  if (!graph || !graph["@graph"] || graph["@graph"].length === 0) return html;

  const scriptTag = `${SCHEMA_TAG_OPEN}${JSON.stringify(graph)}${SCHEMA_TAG_CLOSE}`;

  // Re-run safety: wytnij wczesniejsze ld+json scripts zanim wstrzykniesz nowe.
  const cleaned = html.replace(/<script type="application\/ld\+json">[\s\S]*?<\/script>\n?/gi, "").trim();

  if (!cleaned) return scriptTag;
  return `${scriptTag}\n${cleaned}`;
}

// ───────────────────────────────────────────────────────────
// Validator — blokuje publish gdy graph ma BlogPosting missing required
// ───────────────────────────────────────────────────────────

export function validateSchemaPayload(payload: SchemaPayload): ValidationResult {
  const errors: string[] = [...payload.errors];
  const warnings: string[] = [...payload.warnings];

  if (!payload.graph) {
    // Brak grafu = nothing to validate. Jesli errors puste — intentional skip (schema_enabled=false).
    return {
      isValid: payload.errors.length === 0,
      hasBlockingErrors: payload.errors.length > 0,
      errors,
      warnings,
    };
  }

  const nodes = payload.graph["@graph"];
  const webPage = nodes.find((n) => n["@type"] === "WebPage") as SchemaWebPage | undefined;
  const blog = nodes.find((n) => n["@type"] === "BlogPosting") as SchemaBlogPosting | undefined;

  if (!webPage) {
    errors.push("Graph missing WebPage node");
    return { isValid: false, hasBlockingErrors: true, errors, warnings };
  }

  if (!webPage.url || !/^https?:\/\//.test(webPage.url)) {
    errors.push("WebPage.url not HTTP(S)");
  }
  if (!webPage.name) errors.push("WebPage.name missing");

  if (!blog) {
    errors.push("Graph missing BlogPosting node");
  } else {
    if (!blog.headline || blog.headline.length < 5) errors.push("BlogPosting.headline too short (<5)");
    if (blog.headline && blog.headline.length > 110) warnings.push("BlogPosting.headline > 110 chars");
    if (!blog.description || blog.description.length < 50) warnings.push("BlogPosting.description too short (<50)");
    if (blog.description && blog.description.length > 160) warnings.push("BlogPosting.description > 160 chars");
    if (!blog.url || !/^https?:\/\//.test(blog.url)) errors.push("BlogPosting.url not HTTP(S)");
    if (!blog.datePublished) errors.push("BlogPosting.datePublished missing");
    else if (!/^\d{4}-\d{2}-\d{2}/.test(blog.datePublished)) errors.push("BlogPosting.datePublished not ISO 8601");
    // Author = inline Person object (hybrid). Walidujemy bezpośrednio.
    if (!blog.author) errors.push("BlogPosting.author missing");
    else if (!blog.author.name) errors.push("BlogPosting.author.name missing");
    else {
      const sameAs = blog.author.sameAs || [];
      for (const url of sameAs) {
        if (!url.startsWith("https://")) warnings.push(`Person.sameAs URL not HTTPS: ${url}`);
      }
      if (!blog.author.jobTitle && !blog.author.description) {
        warnings.push("Person missing both jobTitle and description");
      }
    }
  }

  return {
    isValid: errors.length === 0,
    hasBlockingErrors: errors.length > 0,
    errors,
    warnings,
  };
}

// ───────────────────────────────────────────────────────────
// Audit log
// ───────────────────────────────────────────────────────────

export async function logSchemaInjection(
  nodeId: string,
  payload: SchemaPayload,
  sizeBefore: number,
  sizeAfter: number,
  wpPostId?: number,
): Promise<void> {
  try {
    await (supabase as any).from("schema_injection_logs").insert({
      node_id: nodeId,
      wp_post_id: wpPostId || null,
      schemas_injected: payload.graph?.["@graph"] || [],
      validation_warnings: payload.warnings,
      validation_errors: payload.errors,
      final_html_size_before: sizeBefore,
      final_html_size_after: sizeAfter,
    });
  } catch (e) {
    logPublic("SCHEMA", `Audit log insert failed: ${e instanceof Error ? e.message : String(e)}`);
  }
}

// ───────────────────────────────────────────────────────────
// Helpers (exported dla testow)
// ───────────────────────────────────────────────────────────

export function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

export function smartTrim(text: string, max: number): string {
  if (!text || text.length <= max) return text;
  const hardCut = text.slice(0, max);
  const lastSpace = hardCut.lastIndexOf(" ");
  const trimmed = lastSpace > max * 0.7 ? hardCut.slice(0, lastSpace) : hardCut;
  const cleaned = trimmed.replace(/[\s,;:–—-]+$/, "");
  if (/[.!?…]$/.test(cleaned)) return cleaned;
  return cleaned + "…";
}

export function extractCoreEntities(coreEntities: unknown): string[] {
  if (!coreEntities) return [];
  if (Array.isArray(coreEntities)) {
    return coreEntities
      .map((x) => (typeof x === "string" ? x : typeof x === "object" && x !== null && "name" in x ? String((x as Record<string, unknown>).name ?? "") : ""))
      .filter((s) => s.length > 0)
      .slice(0, 10);
  }
  if (typeof coreEntities === "object") return Object.keys(coreEntities as Record<string, unknown>).slice(0, 10);
  return [];
}

export function extractAnswerFromHtml(html: string, questionHeader: string): string | null {
  const escaped = questionHeader.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`<h3[^>]*>\\s*${escaped}\\s*</h3>([\\s\\S]*?)(?=<h[23][\\s>]|$)`, "i");
  const match = html.match(regex);
  if (!match) return null;
  return stripHtml(match[1]);
}

function normalizeLanguage(lang: string | null | undefined): string {
  if (!lang) return "pl-PL";
  const lower = lang.toLowerCase();
  if (lower === "pl" || lower === "pl-pl") return "pl-PL";
  if (lower === "en") return "en-US";
  if (lower.includes("-")) {
    const [a, b] = lower.split("-");
    return `${a}-${b.toUpperCase()}`;
  }
  return `${lower}-${lower.toUpperCase()}`;
}

// ───────────────────────────────────────────────────────────
// Data-quality helpers (Brief A, 2026-05-17 noc)
// ───────────────────────────────────────────────────────────

/**
 * Dates clamp — `dateModified` nigdy nie może być wcześniejsze niż `datePublished`.
 * Priority chain:
 *   1. effective_publish_date (backdated z UI dialog "Zaplanuj daty")
 *   2. published_at (faktyczny moment publishe)
 *   3. fallbackNow (gdy brak — np. pre-publish schema preview)
 *
 * BACKDATE RULE (2026-05-18): gdy effective_publish_date set, dateModified = datePublished.
 * Powód: `thematic_node_content.updated_at` jest Postgres-auto-touched na NOW() przy każdym
 * UPDATE rowa (publish process robi wiele UPDATE'ów). Bez tego ruleu dateModified zawsze
 * pokazywało moment faktycznego publishu (np. 18.05), a datePublished — backdated (np. 12.05).
 * Różnica = 6 dni = footprint dla algorytmu Google ("backdated publish + immediate edit").
 *
 * Świeży backdated publish → modyfikacja = publikacja (te same moment). Późniejsza realna
 * edycja w WP → dateModified może się rozejść z datePublished (osobny tracking via WP
 * post.modified_gmt — TODO).
 */
export function resolveDates(
  content: {
    published_at?: string | null;
    updated_at?: string | null;
    effective_publish_date?: string | null;
  },
  fallbackNow: string = new Date().toISOString(),
): { datePublished: string; dateModified: string } {
  const published = content.effective_publish_date || content.published_at || fallbackNow;

  // Backdate rule: świeży backdated publish → dateModified = datePublished
  if (content.effective_publish_date) {
    return { datePublished: published, dateModified: published };
  }

  // Classic path: dateModified = updated_at (clamp do ≥ datePublished)
  const updated = content.updated_at || published;
  const pubMs = new Date(published).getTime();
  const updMs = new Date(updated).getTime();
  const modified = Number.isFinite(updMs) && Number.isFinite(pubMs) && updMs > pubMs ? updated : published;
  return { datePublished: published, dateModified: modified };
}

/**
 * Description builder — source priority + strip headline prefix + 140–220 word boundary.
 * Returns undefined gdy finalnie length < 40 (lepiej omit niż śmieci).
 */
export function buildDescription(
  meta_description: string | null | undefined,
  seo_description: string | null | undefined,
  final_html: string | null | undefined,
  headline: string,
): string | undefined {
  const TARGET_MAX = 220;
  const TARGET_MIN = 140;
  const FLOOR = 40;

  const candidates: string[] = [];
  if (typeof meta_description === "string" && meta_description.trim()) candidates.push(meta_description);
  if (typeof seo_description === "string" && seo_description.trim()) candidates.push(seo_description);
  if (typeof final_html === "string" && final_html.trim()) {
    const paragraphExcerpt = extractFirstParagraphText(final_html);
    if (paragraphExcerpt) candidates.push(paragraphExcerpt);
    const stripped = stripHtml(final_html);
    if (stripped) candidates.push(stripped);
  }

  for (const raw of candidates) {
    const cleaned = stripHtml(raw).replace(/\s+/g, " ").trim();
    if (!cleaned) continue;
    const headlineNorm = headline.trim().toLowerCase();
    let candidate = cleaned;
    if (headlineNorm && candidate.toLowerCase().startsWith(headlineNorm)) {
      candidate = candidate.slice(headline.trim().length).replace(/^[\s.,;:–—\-?!]+/, "").trim();
    }
    if (candidate.length < FLOOR) continue;
    if (candidate.length <= TARGET_MAX) return candidate;
    const trimmed = smartTrim(candidate, TARGET_MAX);
    if (trimmed.length >= TARGET_MIN) return trimmed;
    // Trimmed too short after word-boundary cut → try next source.
    if (trimmed.length >= FLOOR) return trimmed;
  }

  return undefined;
}

function extractFirstParagraphText(html: string): string {
  const match = html.match(/<p\b[^>]*>([\s\S]*?)<\/p>/i);
  if (!match) return "";
  return stripHtml(match[1]).replace(/\s+/g, " ").trim();
}

/**
 * sameAs validator — UPROSZCZONA (BEZ slug-pattern blacklist).
 * Tylko: string, https://, trim, dedup case-insensitive.
 * Generator NIE umie odróżnić fake od real URL — to robota UI (Brief B) + manualny cleanup.
 */
export function cleanSameAs(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const result: string[] = [];
  for (const item of raw) {
    if (typeof item !== "string") continue;
    const url = item.trim();
    if (!url.startsWith("https://")) continue;
    const key = url.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(url);
  }
  return result;
}

/**
 * Author image validator — reject placeholder URL patterns.
 */
export function cleanAuthorImage(url?: string | null): string | undefined {
  if (!url || typeof url !== "string") return undefined;
  const trimmed = url.trim();
  if (!trimmed.startsWith("https://")) return undefined;
  if (/(placeholder|default[_-]avatar|avatar[_-]placeholder|no[_-]photo|avatar\.png$)/i.test(trimmed)) return undefined;
  return trimmed;
}

/**
 * Canonical URL builder — guard przeciwko `//#`.
 * Zawsze kończy trailing slash, więc `${canonical}#anchor` nigdy nie da `//#`.
 */
export function buildCanonicalUrl(siteBase: string, slug: string): string {
  const base = siteBase.endsWith("/") ? siteBase.slice(0, -1) : siteBase;
  const stripped = (slug || "").replace(/^\/+/, "").replace(/\/+$/, "");
  if (!stripped) return base + "/";
  return `${base}/${stripped}/`;
}
